import React, { useMemo } from 'react';
import { getCoreRowModel, getPaginationRowModel, useReactTable, type PaginationState } from '@tanstack/react-table';
import { Button, Group, Text, ActionIcon, Tooltip } from '@mantine/core';
import { IconPlus, IconFileDownload, IconCloudUpload, IconRefresh } from '@tabler/icons-react';
import Tabla from '../../../globales/componentes/tabla/tabla';
import { obtenerColumnasMoiEscalera, type MoiEscaleraTableMeta } from './constantesTablaMoiEscaleras';
import type { IConfiguracionMoiEscalera } from '../interfaces';

interface TablaMoiEscalerasVistaProps {
  datos: IConfiguracionMoiEscalera[];
  alDescargarPlantilla?: () => void;
  alCargaMasiva?: () => void;
  /** Vuelve a consultar la matriz (descarta una edición en curso). */
  alRecargar?: () => void;
  actualizarFila: (id: string, campo: keyof IConfiguracionMoiEscalera, valor: string | number) => void;
  eliminarFila: (id: string) => void;
  guardarFila?: (id: string) => void;
  filaEnEdicion?: string | null;
  iniciarEdicion?: (id: string) => void;
  cancelarEdicion?: () => void;
  
  agregarFila: () => void;
  mostrarNotificacionObligatorio?: () => void;
  intentoGuardar?: boolean;
  /** Moneda del costo (moneda local del país). */
  monedaLocal?: string;
}

export const TablaMoiEscalerasVista: React.FC<TablaMoiEscalerasVistaProps> = ({
  datos,
  alDescargarPlantilla,
  alCargaMasiva,
  alRecargar,
  actualizarFila,
  eliminarFila,
  guardarFila,
  filaEnEdicion,
  iniciarEdicion,
  cancelarEdicion,
  agregarFila,
  mostrarNotificacionObligatorio,
  intentoGuardar,
  monedaLocal
}) => {
  const columnas = useMemo(() => obtenerColumnasMoiEscalera(), []);

  const meta: MoiEscaleraTableMeta = {
    actualizarFila,
    eliminarFila,
    guardarFila,
    filaEnEdicion,
    iniciarEdicion,
    cancelarEdicion,
    mostrarNotificacionObligatorio,
    intentoGuardar,
    monedaLocal
  };

  // Paginación en el front, igual que M.O.D Ascensores: la matriz se carga entera y solo se pagina la vista.
  const [paginacion, setPaginacion] = React.useState<PaginationState>({ pageIndex: 0, pageSize: 15 });

  const tabla = useReactTable({
    data: datos,
    columns: columnas,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: row => row.Id,
    meta,
    state: { pagination: paginacion },
    onPaginationChange: setPaginacion,
    autoResetPageIndex: false
  });

  // Si se eliminan filas y la página actual queda fuera de rango, ir a la última que exista.
  React.useEffect(() => {
    const ultimaPagina = Math.max(0, Math.ceil(datos.length / paginacion.pageSize) - 1);
    if (paginacion.pageIndex > ultimaPagina) setPaginacion(p => ({ ...p, pageIndex: ultimaPagina }));
  }, [datos.length, paginacion.pageIndex, paginacion.pageSize]);

  // La fila nueva se agrega de primera: ir a la primera página para que quede visible.
  const agregarFilaVisible = () => {
    setPaginacion(p => ({ ...p, pageIndex: 0 }));
    agregarFila();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Group justify="space-between" mb="sm">
        <Text fw={600} size="sm" c="dimmed">
          Tabla Matriz de Mano de Obra Directa de Escaleras
        </Text>
        <Group gap="sm">
          {/* Íconos con el estilo estándar de las tablas de los demás módulos (Tareas, Equipos, Clientes). */}
          <Group gap={4}>
            {alRecargar && (
              <Tooltip zIndex={100000} label="Refrescar" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
                <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alRecargar}>
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip zIndex={100000} label="Descargar Plantilla" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
              <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alDescargarPlantilla} disabled={!alDescargarPlantilla}>
                <IconFileDownload size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip zIndex={100000} label="Carga Masiva" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
              <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alCargaMasiva} disabled={!alCargaMasiva}>
                <IconCloudUpload size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Button color="primary" variant="light" size="xs" radius="lg" leftSection={<IconPlus color="var(--mantine-color-primary-0)" size={12} />} onClick={agregarFilaVisible} disabled={filaEnEdicion !== null && filaEnEdicion !== undefined}><Text fw={500} size="xs">Agregar Fila</Text></Button>
        </Group>
      </Group>

      <div style={{ flex: 1, minHeight: 250 }}>
        <Tabla tabla={tabla} modoEdicion={true} filaEnEdicion={filaEnEdicion} />
      </div>
    </div>
  );
};
